import { Directive, HostListener, Input, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';

// Fully owns navigation for links whose target lives on the home
// page (e.g. "Rólunk" -> .bf-summary, "Árak" -> the price list
// embedded on tablet/mobile). Do NOT combine this with routerLink on
// the same anchor: routerLink has its own click listener that would
// fire alongside this one and race it with a second, conflicting
// router.navigate() call -- use a plain href for the fallback link
// instead (see routeTarget for the "wide enough, has its own page"
// case).
@Directive({
  selector: '[appScrollToSection]',
  standalone: true,
})
export class ScrollToSectionDirective {
  @Input('appScrollToSection') sectionId = '';

  // When set, the scroll behavior only kicks in below this viewport
  // width (e.g. "Árak" scrolls to the embedded price list on
  // tablet/mobile, but routes to routeTarget -- its standalone page
  // -- on desktop). Leave unset to always scroll.
  @Input() scrollBelowBreakpoint?: number;

  // Where to navigate (via the Router, not a native <a> follow) when
  // the viewport is at/above scrollBelowBreakpoint. Ignored if
  // scrollBelowBreakpoint is unset.
  @Input() routeTarget = '/';

  // Set for a target that exists on every route (e.g. the footer's
  // "Kapcsolat" section) -- always just scrolls on whatever page is
  // currently open, in every viewport, never navigates anywhere.
  @Input() global = false;

  private readonly router = inject(Router);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  @HostListener('click', ['$event'])
  protected onClick(event: Event): void {
    if (!this.isBrowser || !this.sectionId) {
      return;
    }

    event.preventDefault();

    if (this.global) {
      this.scrollToSection();
      return;
    }

    if (
      this.scrollBelowBreakpoint !== undefined &&
      window.innerWidth >= this.scrollBelowBreakpoint
    ) {
      this.router.navigateByUrl(this.routeTarget);
      return;
    }

    if (this.router.url === '/' || this.router.url.startsWith('/#')) {
      this.scrollToSection();
      return;
    }

    this.router.navigate(['/']).then(() => {
      this.waitForSectionAndScroll();
    });
  }

  private waitForSectionAndScroll(attemptsLeft = 30): void {
    const element = document.getElementById(this.sectionId);

    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (attemptsLeft <= 0) {
      return;
    }

    requestAnimationFrame(() =>
      this.waitForSectionAndScroll(attemptsLeft - 1),
    );
  }

  private scrollToSection(): void {
    document
      .getElementById(this.sectionId)
      ?.scrollIntoView({ behavior: 'smooth' });
  }
}
